// Helpers for drawing disease boxes on a clean (un-annotated) scan image.
//
// diseaseDetailedList coordinates live in jaw-crop pixel space: the base
// image cropped to jawCoors. This mirrors drawAnnotationsOnImage /
// cropImageUsingCanvas in ml-service, which render the report images.

// jawCoors is stored as a stringified array, e.g. "[12.5 40 610 402]".
export const parseJawCoors = (jawCoors) => {
  if (typeof jawCoors !== "string" || !jawCoors.trim()) return null;
  const nums = jawCoors
    .replace(/\[|\]/g, "")
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number);
  if (nums.length < 4 || nums.some((n) => !Number.isFinite(n))) return null;
  const [x1, y1, x2, y2] = nums;
  if (x2 <= x1 || y2 <= y1) return null;
  return [x1, y1, x2, y2];
};

// Crop rect on the base image. Without image dims the rect is not clamped
// to the image edges (only matters when the jaw box overflows the frame).
export const getJawCropRect = (jaw, imageWidth = Infinity, imageHeight = Infinity) => {
  const [x1, y1, x2, y2] = jaw;
  const x = Math.max(0, x1);
  const y = Math.max(0, y1);
  return {
    x,
    y,
    width: Math.min(imageWidth - x, x2 - x1),
    height: Math.min(imageHeight - y, y2 - y1),
  };
};

// Findings for a detection: diseaseDetailedList (hard tissue) or diseases (soft tissue).
export const getFindings = (detection = {}) =>
  detection.diseaseDetailedList || detection.diseases || [];

const isPoint = (p) =>
  Array.isArray(p) && p.length === 2 && p.every((n) => typeof n === "number");

// Soft tissue coordinates are polygons nested at varying depth; collect
// every array of [x, y] points.
export const extractPolygons = (value, out = []) => {
  if (!Array.isArray(value) || value.length === 0) return out;
  if (value.every(isPoint)) {
    if (value.length >= 3) out.push(value);
    return out;
  }
  value.forEach((v) => extractPolygons(v, out));
  return out;
};

const isBoxList = (coordinates) =>
  Array.isArray(coordinates?.[0]) && typeof coordinates[0][0] === "number" && coordinates[0].length >= 4;

// Map findings into CanvasImage shapes, multiplying by scale.
// Hard tissue gives boxes [x1, y1, x2, y2, ...]; soft tissue gives polygons.
export const toCanvasBoxes = (findings = [], scale = 1) =>
  findings.map((item) => {
    const coordinates = item.coordinates || [];
    const boxes = isBoxList(coordinates) ? coordinates : [];
    const polygons = boxes.length ? [] : extractPolygons(coordinates);
    return {
      name: item.name,
      color: item.color,
      coords: boxes.map(([x1, y1, x2, y2]) => [x1 * scale, y1 * scale, x2 * scale, y2 * scale]),
      polygons: polygons.map((poly) => poly.map(([x, y]) => [x * scale, y * scale])),
    };
  });

/**
 * Decide which image to render for a detection and how to place its boxes.
 * Order: stored cropped base > base cropped in-browser > legacy disease image.
 * @returns {Array<{mode, url, crop?}>} candidates, best first
 */
export const getDetectionImageCandidates = (detection = {}) => {
  // Soft tissue: diseaseFileUrl is the clean crop the polygons were found on.
  if (Array.isArray(detection.diseases)) {
    return detection.diseaseFileUrl ? [{ mode: "softTissue", url: detection.diseaseFileUrl }] : [];
  }
  const jaw = detection.angleDetected === 0 ? null : parseJawCoors(detection.jawCoors);
  const candidates = [];
  if (jaw && detection.croppedBaseFileUrl) {
    candidates.push({ mode: "cropped", url: detection.croppedBaseFileUrl, jaw });
  }
  if (jaw && detection.baseFileUrl) {
    candidates.push({ mode: "base", url: detection.baseFileUrl, jaw });
  }
  if (detection.diseaseFileURL) {
    candidates.push({ mode: "legacy", url: detection.diseaseFileURL });
  }
  return candidates;
};

/**
 * Given a decoded image and its candidate, return what CanvasImage needs.
 * @returns {{ crop?: object, boxScale: number, drawBoxes: boolean }}
 */
export const getDetectionRenderSpec = (candidate, image) => {
  if (candidate.mode === "softTissue") {
    return { boxScale: 1, drawBoxes: true };
  }
  if (candidate.mode === "cropped") {
    // Stored crop is the jaw crop resized to a fixed height.
    const { height } = getJawCropRect(candidate.jaw);
    return { boxScale: image.height / height, drawBoxes: true };
  }
  if (candidate.mode === "base") {
    return {
      crop: getJawCropRect(candidate.jaw, image.width, image.height),
      boxScale: 1,
      drawBoxes: true,
    };
  }
  // Legacy disease image already has boxes burned in.
  return { boxScale: 1, drawBoxes: false };
};
